import { useLanguage } from '@/contexts/AppContext';
import { loginPhoneAuthTranslations } from '@/i18n/loginPage';

interface AgreementSectionProps {
  agreeWhatsApp: boolean;
  setAgreeWhatsApp: (value: boolean) => void;
  agreeTerms: boolean;
  setAgreeTerms: (value: boolean) => void;
  agreePrivacy: boolean;
  setAgreePrivacy: (value: boolean) => void;
  isAllAgreed: boolean;
  onAgreeAll: (checked: boolean) => void;
  termsOfServiceHref: string;
  privacyPolicyHref: string;
  /**
   * LFPDPPP Primary 동의 토글 (#372). 미체크 시 가입 차단.
   * 옵셔널 — 미전달 시 LFPDPPP 섹션 미표시 (기존 호출자 호환).
   */
  agreePrimary?: boolean;
  setAgreePrimary?: (value: boolean) => void;
  /** LFPDPPP Secondary 동의 토글 (#372). 옵트인. */
  agreeSecondary?: boolean;
  setAgreeSecondary?: (value: boolean) => void;
  /** Aviso PDF 링크 (menu#346 정합). */
  avisoHref?: string;
}

/**
 * 체크박스 UI 컴포넌트
 */
function Checkbox({
  checked,
  size = 'normal',
}: {
  checked: boolean;
  size?: 'normal' | 'large';
}) {
  const dimensions = size === 'large' ? 'w-[20px] h-[20px]' : 'w-[18px] h-[18px]';
  const iconSize = size === 'large' ? 'w-3 h-3' : 'w-2.5 h-2.5';
  const borderRadius = size === 'large' ? 'rounded-[5px]' : 'rounded-[4px]';

  return (
    <div
      className={`${dimensions} ${borderRadius} border-2 transition-all duration-200 flex items-center justify-center group-hover:border-primary ${
        checked ? 'border-primary' : 'border-border'
      }`}
    >
      <svg
        className={`${iconSize} text-primary transition-opacity duration-200 ${
          checked ? 'opacity-100' : 'opacity-0'
        }`}
        fill="none"
        viewBox="0 0 12 10"
      >
        <path
          d="M1 5L4.5 8.5L11 1.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

/**
 * 동의 섹션 컴포넌트
 * - 전체 동의
 * - WhatsApp 동의
 * - 서비스 약관 동의
 * - 개인정보 처리방침 동의
 */
export function AgreementSection({
  agreeWhatsApp,
  setAgreeWhatsApp,
  agreeTerms,
  setAgreeTerms,
  agreePrivacy,
  setAgreePrivacy,
  isAllAgreed,
  onAgreeAll,
  termsOfServiceHref,
  privacyPolicyHref,
  agreePrimary,
  setAgreePrimary,
  agreeSecondary,
  setAgreeSecondary,
  avisoHref,
}: AgreementSectionProps) {
  const { locale } = useLanguage();
  const t = loginPhoneAuthTranslations[locale];
  const showLfpdppp = setAgreePrimary !== undefined && setAgreeSecondary !== undefined;
  const primaryLabel = `${t.lfpdpppPrimaryLabel} ${t.requiredSuffix}`;
  const secondaryLabel = `${t.lfpdpppSecondaryLabel} ${t.optionalSuffix}`;
  const avisoLinkLabel = t.privacyNotice;
  return (
    <div className="mb-[clamp(1.5rem,6vw,2rem)] space-y-3">
      {/* Agree All Checkbox */}
      <label className="flex items-start gap-3 cursor-pointer group pb-3 border-b border-border">
        <div className="flex-shrink-0 mt-0.5">
          <input
            type="checkbox"
            checked={isAllAgreed}
            onChange={(e) => onAgreeAll(e.target.checked)}
            className="sr-only"
          />
          <Checkbox checked={isAllAgreed} size="large" />
        </div>
        <div className="flex-1">
          <p className="text-[14px] font-bold text-foreground">{t.agreeAll}</p>
        </div>
      </label>

      {/* WhatsApp Agreement */}
      <label className="flex items-start gap-3 cursor-pointer group">
        <div className="flex-shrink-0 mt-0.5">
          <input
            type="checkbox"
            checked={agreeWhatsApp}
            onChange={(e) => setAgreeWhatsApp(e.target.checked)}
            className="sr-only"
          />
          <Checkbox checked={agreeWhatsApp} />
        </div>
        <div className="flex-1">
          <p className="text-[13px] text-foreground leading-relaxed">{t.whatsappConsent}</p>
        </div>
      </label>

      {/* Terms of Service Agreement */}
      <div className="flex items-start gap-3">
        <label className="flex-shrink-0 mt-0.5 cursor-pointer group">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
            className="sr-only"
          />
          <Checkbox checked={agreeTerms} />
        </label>
        <div className="flex-1">
          <p className="text-[13px] text-foreground leading-relaxed">
            {t.termsAgreePrefix}
            <a
              href={termsOfServiceHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary-hover underline font-medium text-[13px]"
            >
              {t.termsOfServiceLink}
            </a>
            {t.termsAgreeSuffix} {t.requiredSuffix}
          </p>
        </div>
      </div>

      {/* Privacy Policy Agreement */}
      <div className="flex items-start gap-3">
        <label className="flex-shrink-0 mt-0.5 cursor-pointer group">
          <input
            type="checkbox"
            checked={agreePrivacy}
            onChange={(e) => setAgreePrivacy(e.target.checked)}
            className="sr-only"
          />
          <Checkbox checked={agreePrivacy} />
        </label>
        <div className="flex-1">
          <p className="text-[13px] text-foreground leading-relaxed">
            {t.privacyAgreePrefix}
            <a
              href={privacyPolicyHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary-hover underline font-medium text-[13px]"
            >
              {t.privacyPolicyLink}
            </a>
            {t.privacyAgreeSuffix} {t.requiredSuffix}
          </p>
        </div>
      </div>

      {/* LFPDPPP 동의 섹션 (#372) — Primary 필수 / Secondary 옵트인 */}
      {showLfpdppp && (
        <div
          data-testid="lfpdppp-consent-section"
          className="mt-3 pt-3 border-t border-border space-y-3"
        >
          {/* Primary (필수) */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                data-testid="consent-primary-checkbox"
                checked={agreePrimary ?? false}
                onChange={(e) => setAgreePrimary?.(e.target.checked)}
                className="sr-only"
              />
              <Checkbox checked={agreePrimary ?? false} />
            </div>
            <div className="flex-1">
              <p className="text-[13px] text-foreground leading-relaxed">{primaryLabel}</p>
              {avisoHref && (
                <a
                  href={avisoHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-hover underline font-medium text-[12px] mt-1"
                >
                  {avisoLinkLabel}
                </a>
              )}
            </div>
          </label>

          {/* Secondary (옵션) */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                data-testid="consent-secondary-checkbox"
                checked={agreeSecondary ?? false}
                onChange={(e) => setAgreeSecondary?.(e.target.checked)}
                className="sr-only"
              />
              <Checkbox checked={agreeSecondary ?? false} />
            </div>
            <div className="flex-1">
              <p className="text-[13px] text-muted-foreground leading-relaxed">{secondaryLabel}</p>
            </div>
          </label>
        </div>
      )}
    </div>
  );
}
